import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '../hooks/useInView';
import { brands } from '../data/brands';

const BrandCard = ({ brand, delay = 0, useLogos = false }) => {
  const [ref, isInView] = useInView({ threshold: 0.15 });
  const [isHovered, setIsHovered] = useState(false);

  // Logo-only version for homepage - Minimal, curated design
  if (useLogos) {
    return (
      <Link
        ref={ref}
        to={`/brands/${brand.slug}`}
        className="block relative group cursor-pointer h-full"
        style={{
          opacity: isInView ? 1 : 0,
          transform: isInView ? 'scale(1) translateY(0)' : 'scale(0.98) translateY(20px)',
          transition: `opacity 0.8s ease-out ${delay}ms, transform 0.8s ease-out ${delay}ms`,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Minimal Glass Container */}
        <div
          className="h-full p-6 rounded-[20px] transition-all duration-500 flex flex-col items-center justify-center relative"
          style={{
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            backgroundColor: isHovered ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.015)',
            boxShadow: isHovered
              ? `inset 0 0 30px rgba(255,255,255,0.06),
                 inset 0 1px 0 rgba(255,255,255,0.1),
                 0px 8px 32px rgba(0,0,0,0.15)`
              : `inset 0 0 20px rgba(255,255,255,0.03),
                 inset 0 1px 0 rgba(255,255,255,0.06),
                 0px 4px 16px rgba(0,0,0,0.08)`,
            transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
            borderTop: `1px solid rgba(255,255,255,${isHovered ? '0.12' : '0.08'})`,
            borderLeft: `1px solid rgba(255,255,255,${isHovered ? '0.08' : '0.05'})`,
            borderRight: `1px solid rgba(255,255,255,${isHovered ? '0.04' : '0.02'})`,
            borderBottom: `1px solid rgba(255,255,255,${isHovered ? '0.02' : '0.01'})`,
          }}
        >
          {/* Type badge - subtle, top corner */}
          <span
            className="absolute top-4 left-4 text-[9px] text-white/30 uppercase tracking-widest transition-opacity duration-300"
            style={{ opacity: isHovered ? 0.6 : 0.4 }}
          >
            {brand.type === 'licensed' ? 'Licensed' : 'In-House'}
          </span>

          {/* Brand Logo - hero element, centered */}
          <div className="flex-1 flex items-center justify-center py-4">
            <img
              src={brand.logoImage}
              alt={brand.name}
              className="w-auto object-contain transition-all duration-500"
              style={{
                maxHeight: brand.slug === 'tahari' ? '28px'
                  : ['hurley', 'jason-wu'].includes(brand.slug) ? '36px'
                  : ['nicole-miller', 'ccm', 'taryn-rose'].includes(brand.slug) ? '52px'
                  : '42px',
                opacity: isHovered ? 0.85 : 0.6,
                filter: ['hurley', 'jason-wu'].includes(brand.slug) ? 'brightness(0.85)' : 'brightness(0.95)',
                transform: isHovered ? 'scale(1.05)' : 'scale(1)',
              }}
            />
          </div>

          {/* Hover-reveal content */}
          <div
            className="w-full text-center transition-all duration-400"
            style={{
              opacity: isHovered ? 1 : 0,
              transform: isHovered ? 'translateY(0)' : 'translateY(8px)',
              maxHeight: isHovered ? '120px' : '0px',
              overflow: 'hidden',
            }}
          >
            {/* One-liner */}
            <p className="text-white/50 text-[12px] leading-[1.5] mb-2 px-2">{brand.hero.headline}</p>

            {/* Meta */}
            <p className="text-white/30 text-[10px] mb-3">{brand.genderProgram}</p>

            {/* View link */}
            <span className="text-white/50 text-[11px] tracking-wide">Explore →</span>
          </div>

          {/* Minimal bottom indicator when not hovered */}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 transition-opacity duration-300"
            style={{ opacity: isHovered ? 0 : 0.3 }}
          >
            <div className="w-8 h-[2px] bg-white/20 rounded-full" />
          </div>
        </div>
      </Link>
    );
  }

  // Full brand card version with lifestyle imagery (for Brands page)
  return (
    <Link
      ref={ref}
      to={`/brands/${brand.slug}`}
      className="block relative overflow-hidden rounded-2xl group cursor-pointer h-full"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
        transition: `opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Full-bleed brand card image */}
      <div
        className="absolute inset-0 transition-transform duration-500 ease-out"
        style={{
          transform: isHovered ? 'scale(1.03)' : 'scale(1)',
        }}
      >
        <img
          src={brand.coverImage}
          alt={brand.name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Dark gradient overlay */}
      <div
        className="absolute inset-0 transition-opacity duration-300"
        style={{
          background: isHovered
            ? 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 40%, rgba(0,0,0,0.3) 100%)'
            : 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0.2) 100%)',
        }}
      />

      {/* Content overlay */}
      <div
        className="relative h-full p-6 flex flex-col justify-end transition-all duration-300"
        style={{
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        }}
      >
        {/* Top badge */}
        <span
          className="absolute top-5 left-5 text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full"
          style={{
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            color: 'rgba(255,255,255,0.9)',
          }}
        >
          {brand.type === 'licensed' ? 'Licensed' : 'In-House'}
        </span>

        {/* Brand name */}
        <h3 className="text-white text-[22px] font-medium tracking-tight mb-1 drop-shadow-lg">
          {brand.name}
        </h3>

        {/* One-liner */}
        <p className="text-white/80 text-[13px] mb-2 line-clamp-2 drop-shadow-md">{brand.hero.headline}</p>

        {/* Meta line */}
        <p className="text-white/50 text-[11px] mb-3">{brand.genderProgram}</p>

        {/* Chips */}
        <div className="flex flex-wrap gap-2">
          {brand.hero.badges.map((badge) => (
            <span
              key={badge}
              className="px-2.5 py-1 text-[11px] text-white/70 rounded-full"
              style={{
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(4px)',
              }}
            >
              {badge}
            </span>
          ))}
        </div>

        {/* Hover indicator */}
        <div
          className={`mt-4 pt-3 border-t border-white/10 transition-all duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <span className="text-white/80 text-[13px] font-medium">View Brand →</span>
        </div>
      </div>

      {/* Border and shadow on hover */}
      <div
        className="absolute inset-0 rounded-2xl pointer-events-none transition-all duration-300"
        style={{
          border: `1px solid ${isHovered ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
          boxShadow: isHovered
            ? '0 25px 50px rgba(0,0,0,0.5), 0 0 40px rgba(255,255,255,0.05)'
            : '0 4px 20px rgba(0,0,0,0.3)',
        }}
      />
    </Link>
  );
};

const BrandsGrid = ({ showFilters = false, limit = 6, useLogos = false }) => {
  const [selectedFilter, setSelectedFilter] = useState('All');

  const filterOptions = ['All', 'Licensed', 'In-House'];

  const filteredBrands = selectedFilter === 'All'
    ? brands
    : selectedFilter === 'Licensed'
      ? brands.filter(b => b.type === 'licensed')
      : brands.filter(b => b.type === 'in_house');

  const displayBrands = limit ? filteredBrands.slice(0, limit) : filteredBrands;

  // Card height varies based on display mode
  const cardHeight = useLogos ? 'h-[240px]' : 'h-[340px]';

  return (
    <section id="brands" className="bg-[#0a0a0a] py-20 px-6">
      {/* SVG Filter for Liquid Glass Distortion - subtle for readability */}
      <svg className="absolute w-0 h-0" aria-hidden="true">
        <defs>
          <filter id="liquid-glass-distortion" x="-10%" y="-10%" width="120%" height="120%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.008"
              numOctaves="2"
              seed="3"
              result="noise"
            />
            <feGaussianBlur in="noise" stdDeviation="2" result="blur" />
            <feDisplacementMap
              in="SourceGraphic"
              in2="blur"
              scale="3"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>

      <div className="max-w-[1320px] mx-auto">
        {/* Section Header */}
        <div className="text-center mb-14">
          <p className="text-[#6a6a6a] text-[13px] mb-3 uppercase tracking-wider">Portfolio</p>
          <h2 className="text-[42px] md:text-[52px] font-extralight text-white mb-4 tracking-[-0.02em]">
            Our Brands
          </h2>
          <p className="text-[#8a8a8a] text-[15px] leading-[1.7] max-w-[600px] mx-auto">
            Licensed, owned, and developed brands built for real retail, real consumers, and repeatable execution.
          </p>
        </div>

        {/* Category filters (optional) */}
        {showFilters && (
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {filterOptions.map((filter) => (
              <button
                key={filter}
                onClick={() => setSelectedFilter(filter)}
                className="px-4 py-2 rounded-full text-[13px] font-medium transition-all duration-300"
                style={{
                  background: selectedFilter === filter
                    ? 'rgba(255,255,255,0.95)'
                    : 'rgba(255,255,255,0.06)',
                  color: selectedFilter === filter ? '#0a0a0a' : 'rgba(255,255,255,0.7)',
                  border: '1px solid rgba(255,255,255,0.1)',
                }}
              >
                {filter}
              </button>
            ))}
          </div>
        )}

        {/* Brands Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {displayBrands.map((brand, index) => {
            // Sequential card-by-card animation
            const cardDelay = index * 100;

            return (
              <div key={brand.slug} className={cardHeight}>
                <BrandCard brand={brand} delay={cardDelay} useLogos={useLogos} />
              </div>
            );
          })}
        </div>

        {/* CTA to see all */}
        {limit && brands.length > limit && (
          <div className="flex justify-center mt-12">
            <Link
              to="/brands"
              className="px-7 py-3.5 bg-white text-[#0a0a0a] rounded-full text-[15px] font-medium hover:bg-white/90 transition-all duration-300"
            >
              See All Brands
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default BrandsGrid;
